import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Alert, Button, TextArea } from '../../components/ui'
import { keys } from '../../api/keys'
import { sendManualReply } from '../../api/threads'
import { useSession } from '../../auth/session'
import { t } from '../../i18n'

/**
 * Answering a client in your own words.
 *
 * There are days when the manager simply knows the answer — the landlord has
 * just rung, the flat is gone, the client asked something no prompt covers — and
 * waiting for a model to say it back to them is the wrong shape entirely.
 *
 * It is not a second way out of the system. What is typed here becomes a draft
 * whose author is a person and whose approval is the act of writing it, and it
 * leaves through the same door as everything else: the kill switch, the health
 * of the session, the rule that we never write first, and the pace the account
 * is held to all still stand in front of it. What does not apply is the check on
 * the assistant's own wording — a person may quote a price they were just told
 * and promise to call back, which is most of why they are writing it themselves.
 *
 * Two presses, like approving a proposal. The words are already in front of
 * them, but "send to a client" is not a thing this panel does on one tap.
 */
const MAX_RUNES = 4096

const runes = (text: string): number => [...text].length

export function ManualReply({ threadId }: { readonly threadId: string }) {
  const { can } = useSession()
  const client = useQueryClient()

  const [text, setText] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  const send = useMutation({
    mutationFn: () => sendManualReply(threadId, text),
    onSuccess: () => {
      setText('')
      setConfirming(false)
      setFailure(null)
      setSent(true)
      void client.invalidateQueries({ queryKey: keys.thread(threadId) })
      void client.invalidateQueries({ queryKey: keys.anyThreadList() })
    },
    onError: (error: Error) => {
      setConfirming(false)
      setFailure(error.message)
    },
  })

  // Not dimmed, absent. Somebody who may read a conversation is not thereby
  // somebody who may write to the client in it.
  if (!can('drafts.approve')) return null

  const tooLong = runes(text) > MAX_RUNES
  const empty = text.trim() === ''

  return (
    <section className="panel-block">
      <h2 className="panel-block__title">{t('manual.title')}</h2>
      <p className="panel-block__hint">{t('manual.hint')}</p>

      <TextArea
        label={t('manual.label')}
        value={text}
        rows={4}
        disabled={send.isPending}
        placeholder={t('manual.placeholder')}
        hint={t('manual.lengthLimit', { count: String(runes(text)) })}
        error={tooLong ? t('manual.tooLong') : undefined}
        onChange={(event) => {
          setText(event.target.value)
          setConfirming(false)
          setSent(false)
        }}
      />

      {failure === null ? null : <Alert tone="error">{failure}</Alert>}
      {sent ? <Alert tone="info">{t('manual.queued')}</Alert> : null}

      <div className="draft__actions draft__actions--inline">
        {confirming ? (
          <>
            {/*
              The exact words in front of the second press, for the same reason
              the proposal flow shows them: what is about to reach a person
              should be read once more by the one sending it.
            */}
            <Button busy={send.isPending} onClick={() => send.mutate()}>
              {t('manual.confirm')}
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              {t('common.cancel')}
            </Button>
          </>
        ) : (
          <Button
            variant="secondary"
            disabled={empty || tooLong || send.isPending}
            onClick={() => setConfirming(true)}
          >
            {t('manual.send')}
          </Button>
        )}
      </div>
    </section>
  )
}
